/* eslint-disable react-refresh/only-export-components -- the provider, the
   useAuth hook and the token helpers are one coherent module. Splitting them
   across files to gain finer hot-reload granularity would scatter the auth
   layer for a development-time convenience. */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from './supabase';

/**
 * Auth state for the admin, backed by Supabase Auth (docs/overhaul/04).
 *
 * There is only one shared account — the same person logs in from two
 * different workshop PCs — so this deliberately doesn't build out per-user
 * UI; it's a straight swap of the login mechanism underneath the same
 * `{ token, login, logout }` shape the rest of the app already used for the
 * old JWT scheme, so nothing outside this file needed to change.
 *
 * `onAuthStateChange` is the single source of truth for `token`: it fires on
 * sign-in, sign-out, and Supabase's own automatic token refresh, so the app
 * never holds a token past its refresh without needing to poll for it.
 */

type AuthValue = {
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthValue | null>(null);

export class AuthError extends Error {}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setToken(data.session?.access_token ?? null);
      setReady(true);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setToken(session?.access_token ?? null);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      token,
      login: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new AuthError(error.status === 400 ? 'invalid-credentials' : 'server-error');
      },
      logout: () => {
        void supabase.auth.signOut();
      },
    }),
    [token]
  );

  // Avoid a flash of the login screen while the persisted session (if any)
  // is still being restored from sessionStorage on first load.
  if (!ready) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export const readToken = async () => (await supabase.auth.getSession()).data.session?.access_token ?? null;
